<template>
  <div class="container-fluid">
    <div class="card card-default">
      <div class="card-header feature-header">
        <div class="row">
          <div class="col-auto me-auto">
            <CardTitleComponent />
          </div> 
          <div class="col-auto">
            <SearchComponent v-model="searchText" @search="onSearch" @clear="clearSearch" />
          </div>
          <div class="col-auto">
            <CompanyCommonComponent @on-CompanyChange="onCompanyChange" />
          </div>
        </div>     
      </div>
      <div class="card-body card-body-custom-layout">
        <div class="card-body-grid">
          <GridComponent
          ref="baseDataGrid"         
          :dataSource="dataSource"
          :columns="columnDef"
          :autoFit="true"
          :allowPaging="true"
          :allowSorting="true"
          :allowMultiSorting="true"
          :allowResizing="true"
          :searchSettings="searchOptions"
          :pageSettings="pageSettings"       
          :locale="langLocale"   
          :rowHeight="30"
          height="100%"
          :commandClick="onCommandClick"
          :dataStateChange="onDataStateChange"
        >
          <template
            v-for="columnName of templateDef?.columnNames"
            v-slot:[`${templateDef?.prefix}_${columnName}`]="{ data }"
          >
            <slot
              :rowData="{
                field: columnName,
                value: data[columnName],
              }"
              :name="`${templateDef?.prefix}_${columnName}`"
            ></slot>
          </template>
        </GridComponent>
 
        </div>
      </div>
    </div>
  </div>
</template>



<script lang="ts" setup>

import './BaseGridView.Component.css';
// import {getCurrentInstance, ComponentPublicInstance} from 'vue';
import CompanyCommonComponent from "@/components/poweroffice/common/CompanyCommonComponent.vue";
import agent, { powerOfficeServiceBaseURL } from "@/services/agent";
import { L10n, setCulture } from "@syncfusion/ej2-base";
import {
  ColumnModel,
  CommandColumn,
  GridComponent,
  Page,
  Resize,
  Search,
  Sort,
  Toolbar,
} from "@syncfusion/ej2-vue-grids";
import {
  defineProps,
  getCurrentInstance,
  onBeforeMount,
  onMounted,
  provide,
  ref,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import {
  DataProperty,
  GridData,
  GridResponse,
  SearchOption,
  TemplateDef,
} from "./CommonInterfaces";
import CardTitleComponent from '@/components/common/CardTitleComponent.vue';
import SearchComponent from '@/components/shared/SearchComponent.vue';

// grid settings
provide("grid", [Toolbar, Resize, Search, CommandColumn, Page, Sort]);
const toolbar = ["Search"];
const pageSettings = {
  pageSizes: [10, 20, 50, 100],
  pageSize: 50,
  pageCount: 5,
};
const defaultState = {
  skip: 0,
  take: pageSettings.pageSize,
};

interface PropDefinition {
  endpoint: string;
  pageTitle?: string;
  searchOptions?: SearchOption;
  columnDef: ColumnModel[];
  localizedGridData?: object;
  templateDef?: TemplateDef;
  modifyData?: (response: DataProperty) => DataProperty;
  commandClick?: (commandTitle: string, rowData: any) => void;
}

const props = defineProps<PropDefinition>();

const app = getCurrentInstance();

const columnDef = ref(props.columnDef);

onBeforeMount(() => {
  // comInstance = app!.proxy!;

  agent.setBaseUrl(powerOfficeServiceBaseURL);
  localizeGrid();
});

onMounted(() => {
  onDataStateChange(defaultState);
});

const onDataStateChange = (state: any) => {
  localizeGrid();
  fetchData(state);
};

const onCommandClick = (args: { commandColumn: any; rowData: any }) => {
  if (props.commandClick) {
    props.commandClick(args.commandColumn.title, args.rowData);
  }
};

let companyIdUpdated = ref(0);
const onCompanyChange = (companyId: number) => {
  companyIdUpdated.value = companyId;
  fetchData(defaultState);
};

let langLocale = ref("en-grid");
const { locale } = useI18n();
// eslint-disable-next-line no-unused-vars
watch(locale, (_newVal, _oldVal) => {
  localizeGrid();

  columnDef.value = getRevisedColumnDef();
});

const getRevisedColumnDef = () => {
  const instance = app?.proxy;

  let columnDefs = [...props.columnDef];

  columnDefs.forEach((column) => {
    column.headerText = instance?.$t(`${headerReplace(column?.field)}`);
  });

  return columnDefs;
};

const localizeGrid = () => {
  const langGrid = locale.value === "se" ? "se-grid" : "en-grid";
  langLocale.value = langGrid;
  setCulture(langGrid);

  if (langGrid === "se-grid") {
    if (props.localizedGridData) {
      L10n.load({ ...props.localizedGridData });
    } else {
      L10n.load({ ...gridLocal });
    }
  }
};

const baseDataGrid = ref<any | null>(null);
const searchText = ref('');
const searchOptions = ref<SearchOption>({
  fields: [],        // fill with the fields you want to search
  operator: 'contains',
  ignoreCase: true,
  key: ''
});

const onSearch = (text: string) => {
  searchText.value = text;
  searchOptions.value = {
    ...searchOptions.value,
    key: text
  };
  const grid = baseDataGrid.value?.ej2Instances;
  if (grid) {
    grid.search(text);         // Syncfusion global search call
  }
};
const clearSearch = () => {
  searchText.value = '';
  searchOptions.value = {
    ...searchOptions.value,
    key: ''
  };
  const grid = baseDataGrid.value?.ej2Instances;
  if (grid) {
    grid.search('');           // clear search
  }
};
let dataSource = ref({} as GridData);

const fetchData = (state: any) => {
  if (state.action) {
    delete state.action;
  }

  const gridTable: any = baseDataGrid.value;
  if (gridTable) {
    gridTable.hideSpinner();
  }

  agent
    .post<GridResponse>(props.endpoint, {
      ...state,
      companyId: companyIdUpdated.value,
    })
    .then((response) => {
      if (response.result) {
        dataSource.value = response.result;
      }
    })
    .catch((err) => {
      console.error("api-call-error: ", err);
    });
};

// /Some of the columns have TimeOffset in the field Name
/// but not exist in locale, this function removes it
const headerReplace = (header?: string) => {
  return header?.replace("TimeOffset", "")?.replace("isActive", "status");
};

const gridLocal = {
  "se-grid": {
    grid: {
      EmptyRecord: "Inga poster visas",
      Search: "Sök",
      GroupDropArea: "Dra en kolumnrubrik hit för att gruppera din kolumn",
      UnGroup: "Klicka här för att avgruppera",
      EmptyDataPageError:
        "DataPage får inte vara tom vid den första laddningen eftersom kolumner från dataPage finns i AutoGenerate Column Grid",
      Item: "Artikel",
      Items: "Artikel",
    },
    pager: {
      currentPageInfo: "{0} av {1} sidor",
      totalItemsInfo: "({0} inlägg)",
      firstPageTooltip: "Till första sidan",
      lastPageTooltip: "Till sista sidan",
      nextPageTooltip: "Till nästa sida",
      previousPageTooltip: "Tillbaka till sista sidan",
      nextPagerTooltip: "Till nästa personsökare",
      previousPagerTooltip: "Gå till föregående personsökare",
      pagerDropDown: "Objekt per sida",
    },

  },
};
</script>
